import React, { useEffect, useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Element, scroller } from 'react-scroll'
import { ExportLink, LoadingSpinner, Footer } from 'components'
import { BaseMapDisplay } from 'components/map-display'
import { SearchResultsList } from '../components'
import {
  CollapseMapButton,
  ExpandMapControls,
} from '../components/map-collapse-controls'
import { selectors as apiSelectors } from 'lp-redux-api'
import { selectors } from '../reducer'
import { selectors as globalSelectors } from 'global-reducer'
import * as apiActions from 'api-actions'
import * as routerActions from 'react-router-redux'
import {
  buildSearchQueryString,
  displaySubmitFailure,
  parseSearchQueryObject,
  serializeLocation,
  useCommunity,
  useCommunityText,
  pluralize,
} from 'utils'
import { get, size } from 'lodash'
import { LoadingContainer, Select } from 'lp-components'
import { SearchForm } from '../forms'
import { SEARCH_PAGE_SIZE } from 'config'
import SrStatusMessage from 'components/sr-status-message'

const propTypes = {
  mostRecentPublishedSchoolYear: Types.schoolYear.isRequired,
  fetchSearchOptions: PropTypes.func.isRequired,
  fetchSearchResults: PropTypes.func.isRequired,
  fetchSearchMapLocations: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMap: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  push: PropTypes.func.isRequired,
  results: PropTypes.arrayOf(Types.searchResult),
  mapResults: PropTypes.arrayOf(Types.searchResult),
  resultsTotal: PropTypes.number.isRequired,
  searchOptions: PropTypes.object.isRequired,
  isFailedSearch: PropTypes.bool.isRequired,
}

const defaultProps = {}

// Map panel width (as a percentage of the viewport) for each layout state.
// The results grid column count is derived from the current width so dragging
// the resize handle transitions smoothly between the three states.
const MAP_WIDTH = { expanded: 50, partial: 33, collapsed: 0 }
// Below this width the map is treated as fully collapsed (map hidden, 3 tiles).
const COLLAPSE_THRESHOLD = 8
// At/above this width the results render as the original single-column list.
const SINGLE_COLUMN_MIN = 45

const columnsForMapWidth = (width) => {
  if (width <= COLLAPSE_THRESHOLD) return 3
  if (width < SINGLE_COLUMN_MIN) return 2
  return 1
}

const resultText = ({ results, total, loading }) => {
  if (loading) return 'Showing - Matches'
  if (total === 1) return `Showing ${total} Match`
  if (total <= SEARCH_PAGE_SIZE) return `Showing ${total} Matches`

  return `Showing ${size(results)} of ${total} Matches`
}

function Search({
  mostRecentPublishedSchoolYear,
  fetchSearchOptions,
  fetchSearchResults,
  fetchSearchMapLocations,
  isLoading,
  isLoadingMap,
  location: { query: queryHash },
  push,
  results,
  mapResults,
  resultsTotal,
  searchOptions,
  isFailedSearch,
}) {
  const community = useCommunity()
  const hideSchoolSearchToggle = get(community, 'schoolDisabled', false)
  const t = useCommunityText()

  const blockList = useMemo(
    () => get(community, 'search.options.blockList', []),
    [community]
  )

  const {
    query = '',
    type = hideSchoolSearchToggle
      ? Types.PARTNER_SEARCH_TYPE
      : Types.SCHOOL_SEARCH_TYPE,
    filters = {},
    page = 1,
    sortType = Types.RELEVANCE_SORT_TYPE,
    schoolYear = mostRecentPublishedSchoolYear.id,
  } = useMemo(() => parseSearchQueryObject(queryHash), [queryHash])

  const search = useCallback(
    (params, { updateUrl = true } = {}) => {
      if (updateUrl) {
        push({
          pathname: '/search',
          search: buildSearchQueryString(params),
        })
      }

      return fetchSearchResults(params).then(() => {
        scroller.scrollTo('results', { smooth: true })
      })
    },
    [fetchSearchResults]
  )

  // On mount
  useEffect(() => {
    fetchSearchOptions({ blockList })
    search(
      { query, type, filters, page, sortType, schoolYear },
      { updateUrl: false }
    )
  }, [])

  // Fetch all matching locations for the map (independent of list pagination)
  // so every school/partner/teaching artist is shown as a pin. Re-runs only
  // when the search criteria change, not when the list page changes.
  useEffect(() => {
    fetchSearchMapLocations({ query, type, filters, schoolYear })
  }, [query, type, JSON.stringify(filters), schoolYear])

  const filtersAvailable = useMemo(
    () => size(searchOptions) > 0,
    [searchOptions]
  )

  const searchResultLocations = useMemo(() => {
    return mapResults.map((result) => serializeLocation(result.type, result))
  }, [mapResults])

  const [selectedLocation, setSelectedLocation] = useState()
  const selectedMapLocationId = get(selectedLocation, 'resourceId')

  // Resizable map state. `mapWidth` is a viewport percentage; the results
  // column count and which controls are shown are derived from it.
  const [mapWidth, setMapWidth] = useState(MAP_WIDTH.expanded)
  const [isDragging, setIsDragging] = useState(false)
  const columns = columnsForMapWidth(mapWidth)
  const isExpanded = columns === 1

  // Google Maps only recalculates its size/projection on a resize event. The
  // map panel width is animated (see _results.scss `transition: width 0.18s`),
  // so a single nudge fires mid-animation and leaves the map sized to an
  // intermediate width — the settled layout ends up stale and cluster markers
  // render offset. Keep dispatching resize for the animation's duration (plus a
  // small buffer) so the final tile layout and marker overlay match.
  useEffect(() => {
    // Covers the 0.18s width transition with headroom for rAF timing.
    const SETTLE_MS = 260
    const start = performance.now()
    let frameId = window.requestAnimationFrame(function tick(now) {
      window.dispatchEvent(new Event('resize'))
      if (now - start < SETTLE_MS) {
        frameId = window.requestAnimationFrame(tick)
      }
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [mapWidth])

  const handleResizeStart = useCallback((e) => {
    e.preventDefault()
    setIsDragging(true)

    const onMove = (moveEvent) => {
      const clientX = moveEvent.touches
        ? moveEvent.touches[0].clientX
        : moveEvent.clientX
      const percent = (clientX / window.innerWidth) * 100
      const clamped = Math.min(Math.max(percent, 0), MAP_WIDTH.expanded)
      setMapWidth(clamped <= COLLAPSE_THRESHOLD ? 0 : clamped)
    }

    const onEnd = () => {
      setIsDragging(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onEnd)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onEnd)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onEnd)
    document.addEventListener('touchmove', onMove)
    document.addEventListener('touchend', onEnd)
  }, [])

  if (!filtersAvailable) return <LoadingSpinner />

  return (
    <div className="content all-search-results">
      <div className="map-page-header interior-page short">
        <h1>{t('map.searchHeader')}</h1>
        <SearchForm
          searchOptions={searchOptions}
          initialValues={{
            query,
            type,
            schoolYear,
            filters,
          }}
          name="search-form"
          onSubmit={search}
          onSubmitFail={displaySubmitFailure}
          enableRemoveAll
          searchOnTypeChange
          searchOnYearChange
          hasShownSearchResults={true}
          hideTypeToggle={hideSchoolSearchToggle}
        />
      </div>
      <Element
        name="results"
        className={classnames('results-page', {
          'map-collapsed': columns === 3,
          'map-partial': columns === 2,
          'is-dragging': isDragging,
        })}
        style={{
          '--map-width': `${mapWidth}%`,
          '--results-width': `${100 - mapWidth}%`,
        }}
      >
        <section className="search-map">
          {isExpanded && (
            <CollapseMapButton
              onClick={() => setMapWidth(MAP_WIDTH.collapsed)}
            />
          )}
          <LoadingContainer isLoading={isLoadingMap}>
            <BaseMapDisplay
              locations={searchResultLocations}
              autoZoom
              cluster
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
            />
          </LoadingContainer>
        </section>
        <section id="search-results" className="results-list">
          <div className="actions">
            {!isExpanded && (
              <ExpandMapControls
                onExpand={() => setMapWidth(MAP_WIDTH.expanded)}
                onDragStart={handleResizeStart}
              />
            )}
            <SrStatusMessage
              message={
                isLoading
                  ? 'Searching'
                  : `Search completed with ${resultsTotal} ${pluralize(
                      'results',
                      resultsTotal
                    )}`
              }
            />
            <p>
              {resultText({
                results: results,
                total: resultsTotal,
                loading: isLoading,
              })}
            </p>
            <div className="additional-options">
              {!isLoading && results.length > 0 && (
                <React.Fragment>
                  <ExportLink
                    query={query}
                    filters={filters}
                    type={type}
                    schoolYear={schoolYear}
                    className="link-black"
                  >
                    Export List
                  </ExportLink>
                  <Select
                    name="resultsSortType"
                    className="sort"
                    label="Sorted by:"
                    options={[
                      { key: 'Relevance', value: Types.RELEVANCE_SORT_TYPE },
                      { key: 'Alphabet', value: Types.ALPHABETICAL_SORT_TYPE },
                    ]}
                    input={{ value: sortType, name: 'resultsSortType' }}
                    onChange={(e) =>
                      search({
                        query,
                        type,
                        filters,
                        schoolYear,
                        sortType: e.target.value,
                      })
                    }
                    meta={{}}
                  />
                </React.Fragment>
              )}
            </div>
          </div>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <SearchResultsList
              isFailedSearch={isFailedSearch}
              results={results}
              searchPage={page}
              setSearchPage={(newPage) => {
                return search({
                  query,
                  type,
                  filters,
                  sortType,
                  schoolYear,
                  page: newPage,
                })
              }}
              schoolYearFilter={schoolYear}
              resultsTotal={resultsTotal}
              selectedResultId={selectedMapLocationId}
              columns={columns}
            />
          )}
          <Footer />
        </section>
      </Element>
    </div>
  )
}

Search.propTypes = propTypes
Search.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    mostRecentPublishedSchoolYear:
      globalSelectors.mostRecentPublishedSchoolYear(state),
    isLoading: apiSelectors.isLoading(state, apiActions.fetchSearchResults),
    isLoadingMap: apiSelectors.isLoading(
      state,
      apiActions.fetchSearchMapLocations
    ),
    isFailedSearch: apiSelectors.isFailure(
      state,
      apiActions.fetchSearchResults
    ),
    results: selectors.searchResults(state),
    mapResults: selectors.searchMapResults(state),
    resultsTotal: selectors.totalSearchResults(state),
    searchOptions: selectors.searchOptions(state),
  }
}

const mapDispatchToProps = {
  fetchSearchOptions: apiActions.fetchSearchOptions,
  fetchSearchResults: apiActions.fetchSearchResults,
  fetchSearchMapLocations: apiActions.fetchSearchMapLocations,
  push: routerActions.push,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(Search)
